import { useState, type HTMLAttributes, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Section } from '@/components/Section'
import { EventCard } from '@/components/EventCard'
import { placeholders } from '@/lib/skeletonUtils'
import { cn } from '@/lib/utils'
import { agendaBucket, agendaRowId, type AgendaBucket, type WeekStartsOn } from '@/lib/eventCalendar'
import type { Event } from '@/types'

export type AgendaOwner = 'going' | 'interested' | 'friends'

const BUCKETS: AgendaBucket[] = ['week', 'month', 'later']

function bucketTitle(bucket: AgendaBucket): string {
  switch (bucket) {
    case 'week': return fbt('This week', 'Plans agenda heading for events from today to the end of this week')
    case 'month': return fbt('Later this month', 'Plans agenda heading for events after this week but in the current month')
    case 'later': return fbt('Later', 'Plans agenda heading for events after the current month')
  }
}

/**
 * The viewer's and their friends' upcoming events in date order, bucketed by
 * week / month / later. The card's own status and facepile say whose plan it is.
 */
export function PlansAgenda({
  loading,
  events,
  today,
  weekStartsOn,
  leavingIds,
  ownerOf,
  renderCard,
}: {
  loading?: boolean
  events?: Event[]
  today: string
  weekStartsOn: WeekStartsOn
  /** Rows on their way out: they collapse in place before the caller drops them. */
  leavingIds?: Set<string>
  ownerOf?: (event: Event) => AgendaOwner | undefined
  renderCard?: (event: Event) => ReactNode
}) {
  // Rows and sections absent from the previous list grow in; the first list just appears.
  const [seen, setSeen] = useState(() => ({
    events, ids: idSet(events), buckets: bucketSet(events, today, weekStartsOn),
    entering: new Set<string>(), enteringBuckets: new Set<AgendaBucket>(),
  }))
  if (seen.events !== events) {
    const ids = idSet(events)
    const buckets = bucketSet(events, today, weekStartsOn)
    const entering = new Set(seen.ids && ids ? [...ids].filter(id => !seen.ids!.has(id)) : [])
    const enteringBuckets = new Set(seen.buckets && buckets ? [...buckets].filter(b => !seen.buckets!.has(b)) : [])
    setSeen({ events, ids: ids ?? seen.ids, buckets: buckets ?? seen.buckets, entering, enteringBuckets })
  }

  if (loading || !events || !ownerOf || !renderCard) {
    return (
      <Section loading>
        {placeholders(2).map((_, i) => <EventCard key={i} loading />)}
      </Section>
    )
  }

  const byBucket = new Map<AgendaBucket, Event[]>()
  for (const e of events) {
    const b = agendaBucket(e, today, weekStartsOn)
    byBucket.set(b, [...(byBucket.get(b) ?? []), e])
  }

  // A section whose every row is leaving collapses whole, title included, and a
  // new section grows in whole — its rows don't animate on their own inside it.
  return (
    <div className="flex flex-col gap-3">
      {BUCKETS.filter(b => byBucket.has(b)).map(b => {
        const rows = byBucket.get(b)!
        const sectionLeaving = rows.every(e => leavingIds?.has(e.id))
        const sectionEntering = seen.enteringBuckets.has(b)
        return (
          <Collapsible key={b} leaving={sectionLeaving} entering={sectionEntering}>
            <Section title={bucketTitle(b)} testId={`agenda-${b}`}>
              {rows.map(e => (
                <Collapsible key={e.id} id={agendaRowId(e.id)} data-agenda-row data-owner={ownerOf(e)}
                  className="scroll-mt-24" innerClassName="rounded-xl"
                  leaving={!sectionLeaving && leavingIds?.has(e.id)}
                  entering={!sectionEntering && seen.entering.has(e.id)}>
                  {renderCard(e)}
                </Collapsible>
              ))}
            </Section>
          </Collapsible>
        )
      })}
    </div>
  )
}

/** Collapses to nothing (cancelling its parent's `gap-3`) or grows in from it. */
function Collapsible({
  leaving,
  entering,
  className,
  innerClassName,
  children,
  ...rest
}: {
  leaving?: boolean
  entering?: boolean
  className?: string
  innerClassName?: string
  children: ReactNode
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'> & { [data: `data-${string}`]: unknown }) {
  return (
    <div {...rest} inert={leaving}
      className={cn(
        'grid grid-rows-[1fr] transition-[grid-template-rows,opacity,margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
        leaving && 'grid-rows-[0fr] opacity-0 -mt-3',
        entering && 'animate-row-in',
        className,
      )}>
      <div className={cn('min-h-0 overflow-hidden', innerClassName)}>
        {children}
      </div>
    </div>
  )
}

function bucketSet(events: Event[] | undefined, today: string, weekStartsOn: WeekStartsOn): Set<AgendaBucket> | null {
  return events ? new Set(events.map(e => agendaBucket(e, today, weekStartsOn))) : null
}

function idSet(events: Event[] | undefined): Set<string> | null {
  return events ? new Set(events.map(e => e.id)) : null
}
